import {
  Box,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import {
  IconChevronRight,
  IconCoins,
  IconHeadset,
  IconHelpCircle,
  IconLogout,
  IconPencil,
  IconSettings,
  IconStar,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

const CARD_STYLE = {
  borderRadius: 12,
  border: "1px solid rgba(255, 255, 255, 0.18)",
  backgroundColor: "rgba(2, 28, 54, 0.72)",
};

const MENU_ITEM_HOVER = {
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = "#ffc300";
    e.currentTarget.style.color = "var(--mantine-color-primary-7)";
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = "transparent";
    e.currentTarget.style.color = "white";
  },
};

type AccountMenuItem = {
  label: string;
  icon: typeof IconPencil;
  onClick: () => void;
};

function AccountMenuList({ items }: { items: AccountMenuItem[] }) {
  return (
    <Stack gap={2}>
      {items.map((item) => (
        <UnstyledButton
          key={item.label}
          w="100%"
          py={10}
          px="sm"
          onClick={item.onClick}
          style={{
            borderRadius: 8,
            transition: "background-color 0.15s, color 0.15s",
            color: "#ffffff",
          }}
          {...MENU_ITEM_HOVER}
        >
          <Group justify="space-between" wrap="nowrap" c="inherit">
            <Group gap="sm" wrap="nowrap" c="inherit">
              <item.icon size={16} stroke={1.8} color="currentColor" />
              <Text fw={600} size="sm" c="inherit">
                {item.label}
              </Text>
            </Group>
            <IconChevronRight size={14} color="currentColor" style={{ opacity: 0.85 }} />
          </Group>
        </UnstyledButton>
      ))}
    </Stack>
  );
}

function CreditCoinsIllustration() {
  return (
    <svg width="78" height="58" viewBox="0 0 78 58" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="24" cy="46" rx="16" ry="5" fill="rgba(0,0,0,0.25)" />
      <ellipse cx="52" cy="48" rx="18" ry="5" fill="rgba(0,0,0,0.25)" />
      {[0, 1, 2].map((i) => (
        <g key={`left-${i}`}>
          <ellipse cx="24" cy={38 - i * 8} rx="15" ry="5" fill="#1d4ed8" />
          <ellipse cx="24" cy={35 - i * 8} rx="15" ry="5" fill="#2563eb" />
          <ellipse cx="24" cy={32 - i * 8} rx="15" ry="4" fill="#3b82f6" />
        </g>
      ))}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={`right-${i}`}>
          <ellipse cx="52" cy={42 - i * 7} rx="17" ry="5" fill="#1d4ed8" />
          <ellipse cx="52" cy={39 - i * 7} rx="17" ry="5" fill="#2563eb" />
          <ellipse cx="52" cy={36 - i * 7} rx="17" ry="4" fill="#60a5fa" />
        </g>
      ))}
    </svg>
  );
}

type SidebarDesktopFooterProps = {
  creditAvailable: number | null;
  creditLoadFailed: boolean;
  companyName: string;
  gtid: string;
  companyStatus?: string;
  onLogout: () => void;
};

export function SidebarDesktopFooter({
  creditAvailable,
  creditLoadFailed,
  companyName,
  gtid,
  companyStatus,
  onLogout,
}: SidebarDesktopFooterProps) {
  const navigate = useNavigate();
  const [accountMenuOpened, { toggle: toggleAccountMenu, close: closeAccountMenu }] = useDisclosure(false);
  const accountMenuRef = useClickOutside(() => {
    if (accountMenuOpened) closeAccountMenu();
  });

  const handleEditProfile = () => {
    if (companyStatus === "UNDER_REVIEW") {
      sessionStorage.setItem(
        "gt:profileVerificationSummary",
        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
      );
      navigate("/profile", {
        replace: true,
        state: { lockNoticeAt: Date.now() },
      });
      return;
    }
    navigate("/profile/edit");
  };

  const accountMenuItems: AccountMenuItem[] = [
    {
      label: "Edit Profile",
      icon: IconPencil,
      onClick: () => {
        closeAccountMenu();
        handleEditProfile();
      },
    },
    {
      label: "Credits",
      icon: IconCoins,
      onClick: () => {
        closeAccountMenu();
        navigate("/credits");
      },
    },
    {
      label: "Settings",
      icon: IconHelpCircle,
      onClick: () => {
        closeAccountMenu();
        navigate("/settings");
      },
    },
    {
      label: "Logout",
      icon: IconLogout,
      onClick: () => {
        closeAccountMenu();
        onLogout();
      },
    },
  ];

  return (
    <Stack gap="sm">
      <UnstyledButton
        w="100%"
        p="md"
        onClick={() => navigate("/credits")}
        style={{
          ...CARD_STYLE,
          textAlign: "left",
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(2, 28, 54, 0.92)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = CARD_STYLE.backgroundColor;
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs">
          <Group gap="xs" wrap="nowrap">
            <Box
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconStar size={14} color="#ffc300" fill="#ffc300" stroke={1.5} />
            </Box>
            <Text c="white" size="sm" fw={600}>
              Available Credit
            </Text>
          </Group>
        </Group>

        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <Box style={{ minWidth: 0 }}>
            {creditLoadFailed ? (
              <Text size="xl" fw={700} c="white" lh={1.1}>
                —
              </Text>
            ) : creditAvailable === null ? (
              <Loader size="sm" color="white" type="dots" />
            ) : (
              <Text size="32px" fw={700} c="white" lh={1.1}>
                {creditAvailable}
              </Text>
            )}
            <Text c="rgba(255, 255, 255, 0.78)" size="xs" mt={6} lh={1.45} maw={150}>
              Total collaboration credits available
            </Text>
          </Box>
          <Box style={{ flexShrink: 0, lineHeight: 0, marginBottom: -4 }}>
            <CreditCoinsIllustration />
          </Box>
        </Group>
      </UnstyledButton>

      <Box ref={accountMenuRef}>
        <Collapse in={accountMenuOpened}>
          <Box
            mb="xs"
            p="xs"
            style={{
              ...CARD_STYLE,
              backgroundColor: "rgba(2, 28, 54, 0.95)",
              boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.22)",
            }}
          >
            <AccountMenuList items={accountMenuItems} />
          </Box>
        </Collapse>

        <UnstyledButton
          w="100%"
          p="md"
          onClick={toggleAccountMenu}
          aria-label="Open account menu"
          aria-expanded={accountMenuOpened}
          style={{
            ...CARD_STYLE,
            textAlign: "left",
            transition: "background-color 0.2s",
            backgroundColor: accountMenuOpened ? "rgba(2, 28, 54, 0.92)" : CARD_STYLE.backgroundColor,
          }}
          onMouseEnter={(e) => {
            if (!accountMenuOpened) {
              e.currentTarget.style.backgroundColor = "rgba(2, 28, 54, 0.92)";
            }
          }}
          onMouseLeave={(e) => {
            if (!accountMenuOpened) {
              e.currentTarget.style.backgroundColor = CARD_STYLE.backgroundColor;
            }
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Text size="sm" fw={700} c="white" truncate>
                {companyName}
              </Text>
              <Text c="rgba(255, 255, 255, 0.72)" size="xs" truncate mt={2}>
                {gtid}
              </Text>
            </Box>

            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: "rgba(255, 255, 255, 0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconSettings size={18} color="white" />
            </Box>
          </Group>
        </UnstyledButton>
      </Box>

      <UnstyledButton
        w="100%"
        py="sm"
        px="xs"
        onClick={() => navigate("/settings")}
        style={{
          borderRadius: 8,
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <IconHeadset size={22} color="white" stroke={1.6} style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text fw={700} c="white" size="sm">
                Need Help?
              </Text>
              <Text size="xs" c="rgba(255, 255, 255, 0.78)" mt={2}>
                Contact Support
              </Text>
            </Box>
          </Group>
          <IconChevronRight size={16} color="rgba(255, 255, 255, 0.85)" style={{ flexShrink: 0 }} />
        </Group>
      </UnstyledButton>
    </Stack>
  );
}
